<template>
    <ion-page>
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="end">
                    <ion-button @click="close()">
                        <ion-icon slot="icon-only" :icon="closeOutline"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <ion-title>Stock Take: {{ line.stocktake?.reference }}</ion-title>
            </ion-toolbar>
        </ion-header>
        <ion-content :fullscreen="true">
            <div v-if="line && line.item">
                <div>
                    <ion-item>
                        <ion-label class="ion-text-wrap">
                            <h2>Item Number</h2>
                            <p>
                                {{ line.item.item_number }}
                                <ion-text color="success" v-show="itemMatched">
                                    Matched
                                </ion-text>
                            </p>
                        </ion-label>
                        <ion-button size="medium" color="primary" @click="openItemScanner()">
                            <ion-icon slot="icon-only" :icon="barcode"/>
                        </ion-button>
                        <ion-button expand="block" color="primary" size="medium" @click="openItemPicker()">
                            <ion-icon slot="icon-only" :icon="search"/>
                        </ion-button>
                    </ion-item>
                    <ion-item>
                        <ion-label class="ion-text-wrap">
                            <h2>Location</h2>
                            <p>
                                {{ location }}
                                <ion-text color="success" v-show="locationMatched">Matched</ion-text>
                                <ion-text color="success" v-show="newLocationSet">New Location</ion-text>
                            </p>
                        </ion-label>
                        <ion-button size="medium" color="primary" @click="openLocationScanner()">
                            <ion-icon slot="icon-only" :icon="barcode"/>
                        </ion-button>
                    </ion-item>
                </div>
            </div>
            <ion-row>
                <ion-col size="9">
                    <ion-button expand="block" fill="outline" color="dark" @click="$refs['product'].open()">Product Card</ion-button>
                </ion-col>
                <ion-col size="3">
                    <ion-button expand="block" fill="outline" color="dark" :disabled="noStock" @click="calc()"><ion-icon :icon="calculatorOutline" slot="icon-only" /></ion-button>
                </ion-col>
            </ion-row>
            <ion-item v-if="quantity < 0">
                <ion-label>
                    Item is "No Stock"
                </ion-label>
                <ion-checkbox v-model="noStock" />
            </ion-item>
            <div v-if="quantity > -1">
                <big-stepper :min="-1"  size="medium" label="Quantity Found" v-model="quantity"/>
                <big-stepper size="medium" label="B Quantity Found" v-model="b_quantity"/>
                <big-stepper size="medium" label="Case Length" units="cm" v-model="box.length_cm"/>
                <big-stepper size="medium" label="Case Width" units="cm" v-model="box.width_cm"/>
                <big-stepper size="medium" label="Case Height" units="cm" v-model="box.height_cm"/>
                <big-stepper size="medium" label="Case Quantity" v-model="box.case_qty"/>
                <big-stepper size="medium" label="Inner Quantity" v-model="box.inner_qty"/>
                <big-stepper size="medium" label="Case Weight" units="kg" :step="0.01" v-model="box.case_weight_kg"/>
                <big-stepper size="medium" label="Unit Weight" units="kg" :step="0.01" v-model="box.unit_weight_kg"/>
                <ion-item>
                    <ion-label>
                        <h2>Item Barcode</h2>
                        <p v-if="itemBarcode">{{ itemBarcode }}</p>
                        <p v-else>{{ initItemBarcode }}</p>
                    </ion-label>
                    <ion-button size="medium" @click="openSupplierBarcodeScanner()">
                        <ion-icon :icon="barcode" slot="icon-only"/>
                    </ion-button>
                </ion-item>
            </div>
        </ion-content>
        <ion-footer>
            <ion-button v-show="!alreadySaved" color="warning" class="ion-margin-top" size="large" expand="full" @click="submit()">Save</ion-button>
            <ion-row v-show="alreadySaved">
                <ion-col size="9">
                    <ion-button color="warning" class="ion-margin-top" size="large" expand="full" @click="submit()">Update</ion-button>
                </ion-col>
                <ion-col size="3">
                    <ion-button color="warning" class="ion-margin-top" size="large" expand="full" @click="addAdditionalLocation()">
                        <ion-icon :icon="addCircleOutline" />
                    </ion-button>
                </ion-col>
            </ion-row>
        </ion-footer>
        <product-card-modal ref="product" :item="line.item"/>
    </ion-page>
</template>

<script>
import {
    IonButton,
    IonContent,
    IonPage,
    IonFooter,
    IonLabel,
    IonItem,
    IonText,
    IonRow,
    IonCol,
    alertController,
    IonCheckbox
} from '@ionic/vue';
import {IonIcon, IonToolbar, IonHeader, IonButtons, IonTitle} from '@ionic/vue';
import {modalController} from '@ionic/vue';
import BigStepper from "@/components/BigStepper";
import EventBus from "@/EventBus";
import {search, imageOutline, checkmarkCircleOutline, closeOutline, barcode, calculatorOutline, addCircleOutline} from 'ionicons/icons';
import ProductCardModal from "@/components/Modals/ProductCardModal";
import ItemSearchModalHelper from "@/services/ItemSearchModalHelper";
import LocationSearchModalHelper from "@/services/LocationSearchModalHelper";
import {Capacitor} from "@capacitor/core";
import _ from "lodash";

export default {
    props: ['line'],
    inject: ['$http', '$toast', '$validationAlert', '$scanner'],
    name: 'TakeLine',
    components: {
        ProductCardModal,
        BigStepper,
        IonContent, IonPage, IonLabel, IonFooter, IonIcon, IonItem, IonText, IonRow, IonCol,
        IonToolbar, IonHeader, IonButtons, IonButton, IonTitle, IonCheckbox
    },
    watch: {
        quantity(newVal) {
            if (newVal < 0) {
                this.noStock = true;
            }
        },
        noStock(newVal) {
            if (newVal === false) {
                this.quantity = 0;
            }
        }
    },
    computed: {
        initItemBarcode() {
            const mpn = _.find(this.line.item.mpns, ['type', 'supplier_bc']);
            return mpn?.mpn;
        },
        alreadySaved() {
            return this.line.checked_at && this.line.checked_at.length > 0;
        },
        location() {
            if (this.selectedLocation?.id > 0) {
                return this.selectedLocation.code;
            }
            if (this.line.location?.is_unknown) {
                return 'Location not known, or product is new';
            }
            return this.line.location.code;
        },
        newLocationSet() {
            return this.selectedLocation.id && this.line?.location.is_unknown;
        },
        locationMatched() {
            // soft equality test on purpose, avoids a scenario where "1" doesn't equal 1
            return !this.newLocationSet && this.selectedLocation?.code == this.line.location?.code;
        },
        itemMatched() {
            // soft equality test on purpose, avoids a scenario where "1" doesn't equal 1
            return this.selectedItem?.item_number == this.line.item?.item_number;
        }
    },
    data() {
        return {
            noStock: false,
            quantity: this.line.reported_quantity,
            b_quantity: this.line.reported_b_quantity,
            box: {
                width_cm: this.line.item.case_width_cm,
                height_cm: this.line.item.case_height_cm,
                length_cm: this.line.item.case_length_cm,
                unit_weight_kg: this.line.item.unit_weight_kg,
                case_weight_kg: this.line.item.case_weight_kg,
                inner_qty: this.line.item.inner_qty,
                case_qty: this.line.item.case_qty,
            },
            itemBarcode: this.line.checked_at?null:'',
            selectedItem: this.line.checked_at?this.line.item:{},
            selectedLocation: this.line.checked_at?this.line.location:{},
        }
    },
    methods: {
        addAdditionalLocation() {
            alertController.create({
                header: 'Stock take new location',
                message: `Are you sure you want to do another stock take of <strong>${this.line.item.item_number}</strong> in a new location?`,
                buttons: [{
                    text: 'Cancel',
                    role: 'cancel'
                }, {
                    text: 'Continue',
                    handler: () => {
                        LocationSearchModalHelper.open().then(location => {
                            const url = `/api/stocktakes/me/stocktake/${this.line.stocktake.id}/lines`;
                            EventBus().emitter.emit('loading', true);
                            this.$http.post(url, {
                                location: location.data,
                                copy_stock_take_line: this.line
                            }).then(response => {
                                setTimeout(() => {
                                    modalController.dismiss({
                                        reopen: true,
                                        line: response.data
                                    });
                                }, 200);
                            }).catch(error => {
                                this.$validationAlert(error);
                            }).finally(() => {
                                EventBus().emitter.emit('loading', false);
                            });
                        });
                    }
                }]
            }).then(alert => alert.present());
        },
        calc() {
            alertController.create({
                header: 'Calculator',
                inputs:[{
                    placeholder: 'Case quantity',
                    name: 'case_qty',
                    type: 'number'
                },{
                    placeholder: 'Inner quantity',
                    name: 'inner_qty',
                    type: 'number'
                },{
                    placeholder: 'Cases',
                    name: 'count',
                    type: 'number'
                },{
                    placeholder: 'Loose',
                    name: 'loose',
                    type: 'number'
                }],
                buttons: [{
                    role: 'cancel',
                    text: 'Cancel'
                }, {
                    role: 'submit',
                    text: 'Calculate',
                    handler: values => {
                        return values.inner_qty.length > 0 &&
                            values.case_qty.length > 0 &&
                            values.count.length > 0;
                    }
                }]
            }).then(alert => {
                alert.present();
                alert.onDidDismiss().then(data => {
                    if (data.role === 'submit') {
                        const config = data.data.values;
                        const loose = parseInt(config.loose);
                        this.quantity = (config.case_qty * config.count) + (isNaN(loose)?'0':loose);
                        this.box.case_qty = config.case_qty;
                        this.box.inner_qty = config.inner_qty;
                    }
                });
            });
        },
        close() {
            modalController.dismiss()
        },
        submit() {
            EventBus().emitter.emit('loading', true);
            this.$http.put(`/api/stocktakes/lines/${this.line.id}`, {
                // eslint-disable-next-line @typescript-eslint/camelcase
                reported_quantity: this.quantity,
                reported_b_quantity: this.b_quantity,
                location_id: this.selectedLocation.id,
                item_id: this.selectedItem.id,
                case_width_cm: this.box.width_cm,
                case_length_cm: this.box.length_cm,
                case_height_cm: this.box.height_cm,
                case_qty: this.box.case_qty,
                inner_qty: this.box.inner_qty,
                case_weight_kg: this.box.case_weight_kg,
                unit_weight_kg: this.box.unit_weight_kg,
                item_barcode: this.itemBarcode,
                no_stock: this.noStock,
            }).then(() => {
                this.selectedLocation = {};
                this.selectedItem = {};
                this.quantity = 0;
                this.box = {};
                this.$toast.success("Stock updated");
                setTimeout(() => {
                    modalController.dismiss(true);
                }, 250);
            }).catch(error => {
                this.$validationAlert(error);
            }).finally(() => {
                EventBus().emitter.emit('loading', false);
            });
        },
        openItemPicker() {
            ItemSearchModalHelper.open().then(item => {
                this.selectedItem = item.data;
            });
        },
        openLocationPicker() {
            LocationSearchModalHelper.open().then(location => {
                this.selectedLocation = location.data;
            });
        },
        openLocationScanner() {
            if (Capacitor.platform === 'web') {
                this.selectedLocation = {...this.line.location};
                return;
            }
            this.$scanner.scan('location').then(result => {
                this.selectedLocation = result.extra;
            });
        },
        openItemScanner() {
            if (Capacitor.platform === 'web') {
                this.selectedItem = this.line.item;
                return;
            }
            this.$scanner.search().then(result => {
                this.selectedItem = result;
            });
        },
        openSupplierBarcodeScanner() {
            if (Capacitor.platform === 'web') {
                this.itemBarcode = '12345678';
                return;
            }
            this.$scanner.scan().then(result => {
                this.itemBarcode = result.barcode;
            });
        }
    },
    setup() {
        return {
            barcode,
            imageOutline,
            search,
            checkmarkCircleOutline,
            closeOutline,
            addCircleOutline,
            calculatorOutline
        };
    }
}
</script>
<style>
ion-footer {
    background-color: var(--ion-background-color, #fff);
}
</style>

